// TDU stage-map — route geometry processing (pure, browser+node safe).
//
// Defects this corrects, verified against __fixtures__/2027-christies-to-willunga.kml:
//   §6.1 raw KML repeats consecutive vertices (138.47031,-35.13903 twice) → 0-length segments.
//   §6.2 vertices are <10 m apart vs SRTM ~1 m altitude quantisation → per-segment gradient is
//        noise; resample to ~150 m before any gradient classification.
//   §6.3 the ETA clock starts at End-Race-Neutral, not the line START (the line begins in the
//        neutral rollout). distanceForEta = cumulativeDistance - neutralEndKm, clamped at 0.

import length from '@turf/length';
import along from '@turf/along';
import nearestPointOnLine from '@turf/nearest-point-on-line';
import { lineString } from '@turf/helpers';

const EARTH_EPS = 1e-7; // ~1 cm in degrees; treats sub-cm jitter as identical.

/** Remove consecutive duplicate / near-duplicate vertices (§6.1). coords = [[lng,lat,(ele)]]. */
export function dedupeConsecutive(coords, eps = EARTH_EPS) {
    const out = [];
    for (const c of coords) {
        const prev = out[out.length - 1];
        if (!prev || Math.abs(prev[0] - c[0]) > eps || Math.abs(prev[1] - c[1]) > eps) {
            out.push(c);
        }
    }
    return out;
}

/** turf LineString from [[lng,lat,(ele)]] (drops elevation for planar length — §6.6). */
function toLine(coords) {
    return lineString(coords.map((c) => [c[0], c[1]]));
}

/** Total route length in km. */
export function totalLengthKm(coords) {
    if (coords.length < 2) return 0;
    return length(toLine(coords), { units: 'kilometers' });
}

/**
 * Resample to ~`spacingMeters` even spacing (§6.2). Preserves first+last vertex.
 * Returns [{ lng, lat, distanceKm }] where distanceKm is cumulative from the line start.
 */
export function resample(coords, spacingMeters = 150) {
    if (coords.length < 2) {
        return coords.map((c) => ({ lng: c[0], lat: c[1], distanceKm: 0 }));
    }
    const line = toLine(coords);
    const totalKm = length(line, { units: 'kilometers' });
    const stepKm = spacingMeters / 1000;
    const out = [];
    for (let d = 0; d < totalKm; d += stepKm) {
        const pt = along(line, d, { units: 'kilometers' });
        out.push({ lng: pt.geometry.coordinates[0], lat: pt.geometry.coordinates[1], distanceKm: d });
    }
    const end = along(line, totalKm, { units: 'kilometers' });
    out.push({ lng: end.geometry.coordinates[0], lat: end.geometry.coordinates[1], distanceKm: totalKm });
    return out;
}

/**
 * Cumulative distance (km) from the line start for each input vertex.
 * @returns {number[]} same length as coords; coords[0] => 0.
 */
export function cumulativeDistancesKm(coords) {
    const out = [0];
    for (let i = 1; i < coords.length; i++) {
        const seg = length(toLine([coords[i - 1], coords[i]]), { units: 'kilometers' });
        out.push(out[i - 1] + seg);
    }
    return out;
}

/**
 * Distance used for the ETA clock at a given cumulative route distance, accounting for the
 * neutral rollout (§6.3). Points inside the neutral section return 0 (ETA == race start).
 */
export function distanceForEta(cumulativeKm, neutralEndKm = 0) {
    return Math.max(0, cumulativeKm - neutralEndKm);
}

/** [lng, lat] at a cumulative distance (km) along the route, clamped to the line ends. */
export function coordAtKm(coords, km) {
    if (!coords || !coords.length) return null;
    if (coords.length < 2) return [coords[0][0], coords[0][1]];
    const line = toLine(coords);
    const totalKm = length(line, { units: 'kilometers' });
    const pt = along(line, Math.max(0, Math.min(km, totalKm)), { units: 'kilometers' });
    return pt.geometry.coordinates;
}

/**
 * Snap an arbitrary map point to the nearest point on the route and return its cumulative
 * distance from the line start (km) — the hover/tap lookup (plan §5, turf nearestPointOnLine).
 */
export function nearestDistanceKm(coords, lngLat) {
    const snapped = nearestPointOnLine(toLine(coords), [lngLat[0], lngLat[1]], { units: 'kilometers' });
    return snapped.properties.location;
}

/** Equirectangular-approximation distance (km) — fast enough to scan every vertex per hover. */
export function planarKm(aLng, aLat, bLng, bLat) {
    const dx = (aLng - bLng) * Math.cos(((aLat + bLat) / 2) * Math.PI / 180) * 111.32;
    const dy = (aLat - bLat) * 110.574;
    return Math.hypot(dx, dy);
}

/**
 * Every cumulative distance (km) at which the route passes within `threshKm` of a point — one
 * entry per *physical pass*, ascending. This is the circuit primitive: a finishing-circuit KML
 * draws all laps as one continuous line (verified on the 2027 M Stage 1 file — Checker Hill is a
 * single placemark the line passes 3× at km 88.7/123.8/158.9), so `nearestPointOnLine` alone
 * collapses those to one arbitrary lap. We instead group contiguous below-threshold runs of
 * vertices and take each run's closest approach — geometry-driven, so it self-handles the lap that
 * a stage skips (Lobethal: 2 passes, not 3) without any configured lap count.
 *
 * Returns [] when the point is off the timed line (e.g. a vantage point on the neutral spur) — the
 * caller decides whether to fall back to a single snap or suppress the ETA.
 * Pass a precomputed `cumKm` (from cumulativeDistancesKm) on hot paths to avoid recomputing it.
 */
export function passesForPoint(coords, lngLat, { threshKm = 0.12, cumKm } = {}) {
    if (!coords || coords.length < 2) return [];
    const cum = cumKm || cumulativeDistancesKm(coords);
    const [lng, lat] = lngLat;
    const out = [];
    let inRun = false;
    let bestD = Infinity;
    let bestKm = 0;
    for (let i = 0; i < coords.length; i++) {
        const d = planarKm(coords[i][0], coords[i][1], lng, lat);
        if (d <= threshKm) {
            if (!inRun) { inRun = true; bestD = Infinity; }
            if (d < bestD) { bestD = d; bestKm = cum[i]; }
        } else if (inRun) {
            inRun = false;
            out.push(bestKm);
        }
    }
    if (inRun) out.push(bestKm);
    return out.sort((a, b) => a - b);
}

/**
 * Linearly interpolate elevation (m) at a cumulative distance against an elevation profile
 * ([{distanceKm, elevationM}]). Clamps to the endpoints. Returns null if the profile is empty.
 */
export function elevationAtKm(profile, km) {
    if (!profile || profile.length === 0) return null;
    if (km <= profile[0].distanceKm) return profile[0].elevationM;
    const last = profile[profile.length - 1];
    if (km >= last.distanceKm) return last.elevationM;
    for (let i = 1; i < profile.length; i++) {
        if (profile[i].distanceKm >= km) {
            const a = profile[i - 1];
            const b = profile[i];
            const t = (km - a.distanceKm) / ((b.distanceKm - a.distanceKm) || 1);
            return a.elevationM + t * (b.elevationM - a.elevationM);
        }
    }
    return last.elevationM;
}

/**
 * Average gradient (%) over a window centred on `km` — positive = climbing in the direction of
 * travel. Returns null when the route has no elevation profile. A window (default 400 m) is used
 * on purpose: instantaneous SRTM slope is noise (§6.2), and 400 m is the spectator-meaningful
 * "is this a climb worth watching" scale, not an engineering grade.
 */
export function gradientAtKm(profile, km, windowKm = 0.4) {
    if (!profile || profile.length < 2) return null;
    const half = windowKm / 2;
    const lo = Math.max(profile[0].distanceKm, km - half);
    const hi = Math.min(profile[profile.length - 1].distanceKm, km + half);
    const runKm = hi - lo;
    if (runKm <= 0) return null;
    const riseM = elevationAtKm(profile, hi) - elevationAtKm(profile, lo);
    return (riseM / (runKm * 1000)) * 100;
}

/**
 * Average gradient (%) on the APPROACH to `km` — a trailing window ENDING at the point rather than
 * centred on it. For a summit feature (a KOM sits at the crest) a centred window averages the final
 * ascent with the descent just over the top and reads ~flat; the climb the spectators care about is
 * the run UP to the summit. Falls back toward a leading sample near the line start where there isn't
 * a full window behind the point. Default 1 km — the climb's near approach, not an instantaneous grade.
 */
export function gradientApproachKm(profile, km, windowKm = 1.0) {
    if (!profile || profile.length < 2) return null;
    const hi = Math.min(km, profile[profile.length - 1].distanceKm);
    const lo = Math.max(profile[0].distanceKm, hi - windowKm);
    const runKm = hi - lo;
    if (runKm <= 0) return null;
    const riseM = elevationAtKm(profile, hi) - elevationAtKm(profile, lo);
    return (riseM / (runKm * 1000)) * 100;
}

/**
 * Elevation profile from the route's 3D coordinates (the altitude is the 3rd value, which we
 * otherwise drop — §6.6 only warns against elevation-GAIN totals from SRTM, not the profile
 * shape). Returns [{ distanceKm, elevationM }], evenly downsampled to ~maxPoints for a clean
 * SVG path. Returns [] if the route has no usable elevation.
 */
export function elevationProfile(coords, maxPoints = 400) {
    if (!coords || coords.length < 2) return [];
    const hasElevation = coords.some((c) => Number.isFinite(c[2]) && c[2] !== 0);
    if (!hasElevation) return [];
    const cum = cumulativeDistancesKm(coords);
    const pts = coords.map((c, i) => ({ distanceKm: cum[i], elevationM: Number(c[2]) || 0 }));
    if (pts.length <= maxPoints) return pts;
    const step = Math.ceil(pts.length / maxPoints);
    const out = [];
    for (let i = 0; i < pts.length; i += step) out.push(pts[i]);
    if (out[out.length - 1].distanceKm !== pts[pts.length - 1].distanceKm) out.push(pts[pts.length - 1]);
    return out;
}
